// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatBreadcrumbTitle.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ProjectBreadcrumbTitle.test.tsx
import { PencilSimple, Spinner } from "@phosphor-icons/react";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { BREADCRUMB_TITLE_TEXT_CLASS } from "../../../components/BreadcrumbHeaderRoot";

/**
 * Shared, centralized styling for every Ask Puntt breadcrumb rename so the chat
 * and project headers are pixel-identical and cannot drift apart. The resting
 * title is plain text (no box); the editing input is sized to match it.
 */
const RESTING_WRAPPER_CLASS =
  "group/rename relative inline-flex min-w-0 max-w-[400px] items-baseline";
const TITLE_CLASS = cx(BREADCRUMB_TITLE_TEXT_CLASS, "min-w-0 truncate");
const INPUT_CLASS =
  "min-w-0 max-w-[400px] rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-sm font-medium text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8";

interface BreadcrumbInlineRenameProps {
  /** Current (server-canonical) title. */
  title: string;
  /** Only the owner may rename. Non-owners get a plain, read-only title. */
  canEdit: boolean;
  /** True while the rename request is in flight (swaps the pencil for a spinner). */
  isPending: boolean;
  /** Commit a new title. Trimmed + no-op cases are filtered out before this fires. */
  onRename: (nextTitle: string) => void;
  /** Hard cap mirrored from the backend schema. */
  maxLength: number;
  triggerAriaLabel: string;
  pencilAriaLabel: string;
  inputAriaLabel: string;
  readOnlyTestId: string;
  triggerTestId: string;
  pencilTestId: string;
  inputTestId: string;
}

/**
 * Shared inline-rename affordance for the Ask Puntt breadcrumb titles (chat +
 * project). The RESTING state is plain breadcrumb text — no box, border, or
 * background — identical to the original non-editable title. A pencil is
 * revealed ONLY on hover and is absolutely positioned so it floats OVER the
 * adjacent content (the owner/skill badges) without shifting or reflowing the
 * title, mirroring the ticket breadcrumb rename (`TicketInlineEdit` +
 * `routes/tickets/components/Breadcrumbs.tsx` overlay pencil). Click the title
 * or the pencil to edit in place: Enter / blur saves, Escape cancels, a spinner
 * shows while the request is in flight, and errors revert via the mutation's
 * optimistic rollback.
 */
export function BreadcrumbInlineRename({
  title,
  canEdit,
  isPending,
  onRename,
  maxLength,
  triggerAriaLabel,
  pencilAriaLabel,
  inputAriaLabel,
  readOnlyTestId,
  triggerTestId,
  pencilTestId,
  inputTestId,
}: BreadcrumbInlineRenameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards so a single edit session commits at most once and an Escape-driven
  // blur does not also save.
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);

  // Keep the draft in sync when the title changes from elsewhere (e.g. another
  // tab / the sidebar) while we are not actively editing.
  useEffect(() => {
    if (!isEditing) setDraft(title);
  }, [title, isEditing]);

  if (!canEdit) {
    return (
      <span className={TITLE_CLASS} data-testid={readOnlyTestId}>
        {title}
      </span>
    );
  }

  function startEditing() {
    doneRef.current = false;
    cancelledRef.current = false;
    setDraft(title);
    setIsEditing(true);
  }

  function commit() {
    if (doneRef.current || cancelledRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    setIsEditing(false);
    if (!next || next === title.trim()) {
      setDraft(title);
      return;
    }
    onRename(next);
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(title);
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        maxLength={maxLength}
        aria-label={inputAriaLabel}
        data-testid={inputTestId}
        className={INPUT_CLASS}
      />
    );
  }

  return (
    <span className={RESTING_WRAPPER_CLASS}>
      <button
        type="button"
        onClick={startEditing}
        aria-label={triggerAriaLabel}
        data-testid={triggerTestId}
        className="flex min-w-0 cursor-pointer items-baseline border-none bg-transparent p-0 text-left"
      >
        <span className={TITLE_CLASS} data-testid={readOnlyTestId}>
          {title}
        </span>
      </button>
      <button
        type="button"
        onClick={startEditing}
        aria-label={pencilAriaLabel}
        data-testid={pencilTestId}
        className="absolute left-full top-1/2 ml-1 flex -translate-y-1/2 cursor-pointer items-center rounded border-none bg-transparent p-0.5 text-puntt-neutral-gray-9 opacity-0 transition-opacity hover:text-puntt-neutral-gray-11 focus-visible:opacity-100 group-hover/rename:opacity-100"
      >
        {isPending ? (
          <Spinner size={18} aria-hidden="true" className="animate-spin" />
        ) : (
          <PencilSimple size={18} aria-hidden="true" />
        )}
      </button>
    </span>
  );
}
